import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { LayoutDashboard, Users, MessagesSquare, LogOut, Trash2, Mail, Phone, Building2, TrendingUp, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";

const STATUSES = ["new", "contacted", "qualified", "won", "lost"];
const statusColor = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-amber-100 text-amber-700",
  qualified: "bg-violet-100 text-violet-700",
  won: "bg-emerald-100 text-emerald-700",
  lost: "bg-slate-200 text-slate-600",
};

const StatCard = ({ label, value, icon: Icon, accent }) => (
  <div data-testid={`stat-${label}`} className="rounded-xl border border-slate-200 bg-white p-6">
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-500">{label}</span>
      <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent}`}><Icon className="h-4 w-4" /></div>
    </div>
    <p className="mt-4 font-display text-3xl font-black tracking-tight text-slate-900">{value}</p>
  </div>
);

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("leads");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [convos, setConvos] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([api.get("/leads/stats"), api.get("/leads")]);
      setStats(s.data); setLeads(l.data);
    } catch { toast.error("Failed to load data"); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === "chats") api.get("/conversations").then((r) => setConvos(r.data)).catch(() => {}); }, [tab]);

  const changeStatus = async (id, status) => {
    try { await api.patch(`/leads/${id}`, { status }); setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l))); load(); toast.success(`Marked ${status}`); }
    catch { toast.error("Update failed"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/leads/${id}`); setLeads((ls) => ls.filter((l) => l.id !== id)); setSelected(null); load(); toast.success("Lead deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const filtered = leads.filter((l) => `${l.name} ${l.email} ${l.company} ${l.service}`.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-slate-200 bg-white p-6 lg:flex">
        <Logo />
        <nav className="mt-10 space-y-1">
          {[{ id: "leads", label: "Leads", icon: Users }, { id: "chats", label: "Conversations", icon: MessagesSquare }].map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)} className={`flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${tab === t.id ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="mt-auto">
          <div className="mb-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">Signed in as<br /><span className="font-semibold text-slate-800">{user?.email}</span></div>
          <button data-testid="logout-button" onClick={logout} className="flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100"><LogOut className="h-4 w-4" /> Log out</button>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4 lg:px-10">
          <div className="flex items-center gap-2"><LayoutDashboard className="h-5 w-5 text-brand" /><h1 className="font-display text-xl font-bold text-slate-900">Dashboard</h1></div>
          <button onClick={logout} className="lg:hidden text-sm text-slate-600">Log out</button>
        </header>

        <main className="p-6 lg:p-10">
          {stats && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard label="Total Leads" value={stats.total} icon={Users} accent="bg-brand/10 text-brand" />
              <StatCard label="New (unhandled)" value={stats.by_status.new} icon={Mail} accent="bg-blue-100 text-blue-600" />
              <StatCard label="Won" value={stats.by_status.won} icon={TrendingUp} accent="bg-emerald-100 text-emerald-600" />
              <StatCard label="Last 7 days" value={stats.last_7_days} icon={MessagesSquare} accent="bg-violet-100 text-violet-600" />
            </div>
          )}

          {tab === "leads" && (
            <div className="mt-8 rounded-xl border border-slate-200 bg-white">
              <div className="flex items-center justify-between gap-4 border-b border-slate-200 p-4">
                <h2 className="font-display text-lg font-bold text-slate-900">Leads</h2>
                <div className="relative w-64 max-w-full">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input data-testid="lead-search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search leads…" className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-brand" />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr><th className="px-5 py-3">Name</th><th className="px-5 py-3">Service</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Date</th><th className="px-5 py-3"></th></tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-400">No leads yet.</td></tr>}
                    {filtered.map((l) => (
                      <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => setSelected(l)} className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-slate-50">
                        <td className="px-5 py-4"><p className="font-semibold text-slate-900">{l.name}</p><p className="text-xs text-slate-500">{l.email}</p></td>
                        <td className="px-5 py-4 text-slate-600">{l.service}</td>
                        <td className="px-5 py-4"><span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${statusColor[l.status]}`}>{l.status}</span></td>
                        <td className="px-5 py-4 text-slate-500">{new Date(l.created_at).toLocaleDateString()}</td>
                        <td className="px-5 py-4 text-right"><button data-testid={`delete-lead-${l.id}`} onClick={(e) => { e.stopPropagation(); remove(l.id); }} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "chats" && (
            <div className="mt-8 space-y-4">
              {convos.length === 0 && <div className="rounded-xl border border-slate-200 bg-white p-12 text-center text-slate-400">No conversations yet.</div>}
              {convos.map((c) => (
                <div key={c.session_id} data-testid={`convo-${c.session_id}`} className="rounded-xl border border-slate-200 bg-white p-5">
                  <p className="mb-3 text-xs font-mono text-slate-400">{c.session_id} · {new Date(c.created_at).toLocaleString()}</p>
                  <div className="space-y-2">
                    {(c.messages || []).map((m, i) => (
                      <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <span className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "bg-brand text-white" : "bg-slate-100 text-slate-800"}`}>{m.content}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {selected && (
        <div className="fixed inset-0 z-[70] flex justify-end bg-slate-900/40" onClick={() => setSelected(null)}>
          <div data-testid="lead-detail-panel" className="h-full w-full max-w-md overflow-y-auto bg-white p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div><h3 className="font-display text-2xl font-bold text-slate-900">{selected.name}</h3><span className={`mt-2 inline-block rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${statusColor[selected.status]}`}>{selected.status}</span></div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-900">✕</button>
            </div>
            <div className="mt-6 space-y-3 text-sm">
              <p className="flex items-center gap-3 text-slate-700"><Mail className="h-4 w-4 text-brand" /> {selected.email}</p>
              {selected.phone && <p className="flex items-center gap-3 text-slate-700"><Phone className="h-4 w-4 text-brand" /> {selected.phone}</p>}
              {selected.company && <p className="flex items-center gap-3 text-slate-700"><Building2 className="h-4 w-4 text-brand" /> {selected.company}</p>}
            </div>
            <div className="mt-6 rounded-xl bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Service</p><p className="mt-1 text-slate-800">{selected.service}</p>
              <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Budget</p><p className="mt-1 text-slate-800">{selected.budget || "—"}</p>
              <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Message</p><p className="mt-1 leading-relaxed text-slate-700">{selected.message}</p>
            </div>
            <div className="mt-6">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Update status</p>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button key={s} data-testid={`set-status-${s}`} onClick={() => changeStatus(selected.id, s)} className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize transition ${selected.status === s ? "bg-brand text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{s}</button>
                ))}
              </div>
            </div>
            <button onClick={() => remove(selected.id)} className="mt-8 flex items-center gap-2 text-sm font-medium text-red-500 hover:text-red-600"><Trash2 className="h-4 w-4" /> Delete lead</button>
          </div>
        </div>
      )}
    </div>
  );
}
